import React, { useState, useEffect, useCallback, useRef } from 'react';
import { leadsApi, employeesApi, utilityApi, vehicleApi, inspectionPackagesApi, partnersApi, adCityMappingsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateTime, formatDate, formatTime } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlacesAutocomplete } from '@/components/ui/PlacesAutocomplete';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { 
  Search, Plus, Pencil, Loader2, X, Users, TrendingUp, Calendar, 
  Phone, MapPin, Bell, Clock, CreditCard, Copy, ChevronLeft, ChevronRight, Filter,
  MessageCircle, Link2, ExternalLink, Eye, Flame, ChevronDown, Percent, CalendarDays,
  StickyNote, Activity, Send, FileText, Car, Trash2, RefreshCw, CheckCircle, Gift, Info, Mail, History, User
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

// Import extracted lead components
import { StatusDropdown, getStatusConfig } from '@/components/leads/StatusDropdown';
import { ActivityLog } from '@/components/leads/ActivityLog';
import { LeadStats } from '@/components/leads/LeadStats';
import { LeadFilters } from '@/components/leads/LeadFilters';
import { DateRangeFilter, getDateRange, DATE_PRESETS } from '@/components/ui/DateRangeFilter';

export default function LeadsPage() {
  const { user } = useAuth();
  
  // Check if user is a sales executive (only sees their own leads)
  const isSalesExec = user?.role_code === 'SALES_EXEC';
  
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cities, setCities] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [cityCounts, setCityCounts] = useState({});
  const [employeeCounts, setEmployeeCounts] = useState({});
  const [cardCounts, setCardCounts] = useState({});
  const [inspectionPackages, setInspectionPackages] = useState([]); // Active packages from settings
  const [availableOffers, setAvailableOffers] = useState([]); // Active offers for payment modal
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaymentLinksModalOpen, setIsPaymentLinksModalOpen] = useState(false);
  const [paymentLinksData, setPaymentLinksData] = useState(null);
  const [loadingPaymentLinks, setLoadingPaymentLinks] = useState(false);
  const [resendingSms, setResendingSms] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [assigningLead, setAssigningLead] = useState(null);
  const [reminderLead, setReminderLead] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [activeFilter, setActiveFilter] = useState('all');

  // Sales reps for assignment (filtered by city)
  const [salesRepsForCity, setSalesRepsForCity] = useState([]);
  const [loadingSalesReps, setLoadingSalesReps] = useState(false);

  // Sales employees for the "Assigned To" dropdown in Create Lead form
  const [salesEmployeesForForm, setSalesEmployeesForForm] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterSource, setFilterSource] = useState('');
  // Get today's date for filtering (in local timezone, not UTC)
  const getLocalDate = () => {
    const now = new Date();
    // Format as YYYY-MM-DD in local timezone
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };
  const today = getLocalDate();
  const [filterDateFrom, setFilterDateFrom] = useState(today);
  const [filterDateTo, setFilterDateTo] = useState(today);
  const [dateFilterPreset, setDateFilterPreset] = useState('today');

  // Filtered employees - only sales roles and those with leads assigned
  const filteredEmployees = employees.filter(emp => {
    const salesRoles = ['SALES_EXEC', 'SALES_LEAD', 'SALES_HEAD', 'COUNTRY_HEAD'];
    // Include if has a sales role OR if this employee has leads assigned to them
    const hasSalesRole = salesRoles.includes(emp.role_code);
    const hasLeadsAssigned = leads.some(l => l.assigned_to === emp.name || l.assigned_to === emp.id);
    return hasSalesRole || hasLeadsAssigned;
  });

  // Filtered cities - only from AD mappings or cities with leads
  const [adMappedCities, setAdMappedCities] = useState([]);
  const filteredCities = [...new Set([
    ...adMappedCities,
    ...leads.map(l => l.city).filter(Boolean)
  ])].sort();

  // Partners list for selection
  const [partners, setPartners] = useState([]);
  const [defaultB2CPartner, setDefaultB2CPartner] = useState(null);

  const [formData, setFormData] = useState({
    name: '', mobile: '', email: '', city: '', source: 'WEBSITE', status: 'NEW LEAD',
    assigned_to: '', reminder_date: '', reminder_time: '', notes: '',
    service_type: '', ad_id: '', partner_id: '', partner_name: '',
  });

  const [reminderFormData, setReminderFormData] = useState({
    reminder_date: '', reminder_time: '', reminder_reason: '', notes: '',
  });

  const [paymentFormData, setPaymentFormData] = useState({
    hasCarDetails: 'yes', carNo: '', carMake: '', carModel: '', carYear: '',
    fuelType: '', carColor: '', carConfirmed: false, packageId: '',
    discountType: '', discountValue: '', city: '',
    customerMobile: '', customerName: '',
    selectedOfferIds: [], // Offers selected by sales head
    usePartialPayment: false, // Whether to use partial payment for this transaction
  });

  // Multiple inspection schedules - one per inspection slot in the package
  const [inspectionSchedules, setInspectionSchedules] = useState([]);

  const [carLoading, setCarLoading] = useState(false);
  const [carError, setCarError] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  
  // Ad Info Modal state
  const [adInfoModal, setAdInfoModal] = useState({ open: false, lead: null });

  // Notes and Activities states
  const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState(false);
  const [selectedLeadForNotes, setSelectedLeadForNotes] = useState(null);
  const [leadNotes, setLeadNotes] = useState([]);
  const [leadActivities, setLeadActivities] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [notesTab, setNotesTab] = useState('notes'); // 'notes' or 'activities'
  const [vehicleData, setVehicleData] = useState(null); // Full vehicle data from Vaahan
  
  // Initialize inspection schedules when package changes
  const initializeInspectionSchedules = useCallback((pkg) => {
    if (!pkg) {
      setInspectionSchedules([]);
      return;
    }
    const count = pkg.no_of_inspections || 1;
    const todayDate = getLocalDate(); // Default date to today (local timezone)
    const newSchedules = Array.from({ length: count }, (_, index) => ({
      id: `schedule-${index}`,
      vehicleNumber: index === 0 && paymentFormData.carNo ? paymentFormData.carNo : '',
      vehicleData: index === 0 && vehicleData ? vehicleData : null,
      vehicleConfirmed: index === 0 && paymentFormData.carConfirmed,
      inspectionDate: todayDate,
      inspectionTime: '',
      address: '',
      latitude: '',
      longitude: '',
      isLoading: false,
      error: '',
    }));
    setInspectionSchedules(newSchedules);
  }, [paymentFormData.carNo, paymentFormData.carConfirmed, vehicleData]);

  // Update schedule field
  const updateSchedule = (index, field, value) => {
    setInspectionSchedules(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Fetch vehicle details for a specific schedule
  const fetchVehicleForSchedule = async (index, vehicleNumber) => {
    if (!vehicleNumber) return;
    
    updateSchedule(index, 'isLoading', true);
    updateSchedule(index, 'error', '');
    
    try {
      const response = await vehicleApi.getDetails(vehicleNumber);
      const data = response.data;
      
      if (data.success && data.data) {
        updateSchedule(index, 'vehicleData', data.data);
        updateSchedule(index, 'vehicleConfirmed', false);
        toast.success(`Vehicle ${index + 1} details fetched successfully`);
      } else {
        updateSchedule(index, 'error', data.error || 'Failed to fetch vehicle details');
        toast.error(data.error || 'Failed to fetch vehicle details');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch vehicle details';
      updateSchedule(index, 'error', errorMsg);
      toast.error(errorMsg);
    } finally {
      updateSchedule(index, 'isLoading', false);
    }
  };

  // Fetch car details from Vaahan API
  const fetchCarDetails = async (carNumber) => {
    setCarLoading(true);
    setCarError('');
    setVehicleData(null);
    try {
      const response = await vehicleApi.getDetails(carNumber);
      const data = response.data;
      
      if (data.success && data.data) {
        const vehicle = data.data;
        // Store full vehicle data for saving later
        setVehicleData(vehicle);
        
        // Update form with key details for display
        setPaymentFormData(prev => ({ 
          ...prev, 
          carMake: vehicle.manufacturer || '',
          carModel: vehicle.model || '',
          carYear: vehicle.manufacturing_date ? vehicle.manufacturing_date.split('/')[1] : '',
          fuelType: vehicle.fuel_type || '',
          carColor: vehicle.color || '',
          // Additional fields
          chassisNumber: vehicle.chassis_number || '',
          engineNumber: vehicle.engine_number || '',
          registrationDate: vehicle.registration_date || '',
          rcExpiryDate: vehicle.rc_expiry_date || '',
          ownerName: vehicle.owner_name || '',
          insuranceCompany: vehicle.insurance_company || '',
          insuranceValidUpto: vehicle.insurance_valid_upto || '',
        }));
        toast.success('Vehicle details fetched successfully from Vaahan');
      } else {
        setCarError(data.error || 'Failed to fetch vehicle details');
        toast.error(data.error || 'Failed to fetch vehicle details');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch vehicle details. Please check the registration number.';
      setCarError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setCarLoading(false);
    }
  };

  // Save vehicle to vehicle master
  const saveVehicleToMaster = async (leadId = null) => {
    if (!vehicleData) return null;
    
    try {
      const response = await vehicleApi.save({
        ...vehicleData,
        lead_id: leadId || selectedLead?.id || '',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to save vehicle:', error);
      return null;
    }
  };

  // Fetch notes and activities for a lead
  const fetchNotesAndActivities = async (lead) => {
    setLoadingNotes(true);
    try {
      const [notesRes, activitiesRes] = await Promise.all([
        leadsApi.getNotes(lead.id),
        leadsApi.getActivities(lead.id)
      ]);
      setLeadNotes(notesRes.data || []);
      setLeadActivities(activitiesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch notes/activities:', error);
      setLeadNotes([]);
      setLeadActivities([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Open notes drawer
  const openNotesDrawer = async (lead) => {
    setSelectedLeadForNotes(lead);
    setIsNotesDrawerOpen(true);
    setNotesTab('notes');
    setNewNote('');
    await fetchNotesAndActivities(lead);
  };

  // Add a new note with debounce protection
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLeadForNotes || savingNote) return;
    
    const noteText = newNote.trim();
    setNewNote(''); // Clear immediately to prevent double submission
    setSavingNote(true);
    
    try {
      await leadsApi.addNote(selectedLeadForNotes.id, noteText);
      toast.success('Note added successfully');
      await fetchNotesAndActivities(selectedLeadForNotes);
    } catch (error) {
      setNewNote(noteText); // Restore note on error
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  // Load static reference data once on mount (employees, cities, sources, statuses, packages, etc.)
  const staticDataLoaded = useRef(false);
  useEffect(() => {
    if (staticDataLoaded.current) return;
    staticDataLoaded.current = true;
    const loadStaticData = async () => {
      try {
        const [employeesRes, citiesRes, sourcesRes, statusesRes, packagesRes, offersRes, partnersRes, adMappingsRes] = await Promise.all([
          employeesApi.getAll(), utilityApi.getCities(),
          utilityApi.getLeadSources(), utilityApi.getLeadStatuses(),
          inspectionPackagesApi.getPackages(user?.country_id),
          inspectionPackagesApi.getActiveOffers(user?.country_id),
          partnersApi.getPartners({ is_active: true }),
          adCityMappingsApi.getAll().then(r => r.data).catch(() => []),
        ]);
        setEmployees(employeesRes.data);
        setCities(citiesRes.data);
        setSources(sourcesRes.data);
        setStatuses(statusesRes.data || []);
        const activePackages = (packagesRes.data || [])
          .filter(p => p.is_active)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setInspectionPackages(activePackages);
        setAvailableOffers(offersRes.data || []);
        const loadedPartners = partnersRes.data || [];
        setPartners(loadedPartners);
        const b2cPartner = loadedPartners.find(p => p.type === 'b2c' || p.name?.toLowerCase().includes('b2c'));
        setDefaultB2CPartner(b2cPartner || null);
        const adMappingsArray = Array.isArray(adMappingsRes) ? adMappingsRes : [];
        const mappedCities = [...new Set(adMappingsArray.map(m => m.city).filter(Boolean))];
        setAdMappedCities(mappedCities);
      } catch (error) {
        console.error('Failed to load static data:', error);
      }
    };
    loadStaticData();
  }, [user?.country_id]);

  // Fetch dynamic data (leads + counts) — runs on filter/page changes
  const fetchData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterEmployee && filterEmployee !== 'all') params.assigned_to = filterEmployee;
      if (filterStatus && filterStatus !== 'all') params.lead_status = filterStatus;
      if (filterCity && filterCity !== 'all') params.city = filterCity;
      if (filterSource && filterSource !== 'all') params.source = filterSource;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (activeFilter && activeFilter !== 'all') params.active_filter = activeFilter;
      params.page = currentPage;
      params.limit = itemsPerPage;

      const [leadsRes, statusCountsRes, cityCountsRes, employeeCountsRes, cardCountsRes] = await Promise.all([
        leadsApi.getAll(params),
        utilityApi.getLeadStatusCounts({
          ...(filterDateFrom ? { date_from: filterDateFrom } : {}),
          ...(filterDateTo ? { date_to: filterDateTo } : {}),
          ...(filterEmployee && filterEmployee !== 'all' ? { assigned_to: filterEmployee } : {}),
          ...(filterCity && filterCity !== 'all' ? { city: filterCity } : {}),
        }),
        // Fetch city counts (same date/employee/status filters, but NOT city filter)
        utilityApi.getLeadCityCounts({
          ...(filterDateFrom ? { date_from: filterDateFrom } : {}),
          ...(filterDateTo ? { date_to: filterDateTo } : {}),
          ...(filterEmployee && filterEmployee !== 'all' ? { assigned_to: filterEmployee } : {}),
          ...(filterStatus && filterStatus !== 'all' ? { lead_status: filterStatus } : {}),
        }),
        // Fetch employee counts (same date/status/city filters, but NOT employee filter)
        utilityApi.getLeadEmployeeCounts({
          ...(filterDateFrom ? { date_from: filterDateFrom } : {}),
          ...(filterDateTo ? { date_to: filterDateTo } : {}),
          ...(filterStatus && filterStatus !== 'all' ? { lead_status: filterStatus } : {}),
          ...(filterCity && filterCity !== 'all' ? { city: filterCity } : {}),
        }),
        // Fetch card counts (same base filters, NOT active_filter)
        utilityApi.getLeadCardCounts({
          ...(search ? { search } : {}),
          ...(filterEmployee && filterEmployee !== 'all' ? { assigned_to: filterEmployee } : {}),
          ...(filterStatus && filterStatus !== 'all' ? { lead_status: filterStatus } : {}),
          ...(filterCity && filterCity !== 'all' ? { city: filterCity } : {}),
          ...(filterSource && filterSource !== 'all' ? { source: filterSource } : {}),
          ...(filterDateFrom ? { date_from: filterDateFrom } : {}),
          ...(filterDateTo ? { date_to: filterDateTo } : {}),
        }),
      ]);

      // Handle paginated response
      const leadsData = leadsRes.data;
      setLeads(leadsData.data || []);
      setTotalLeads(leadsData.total || 0);
      setTotalPages(leadsData.total_pages || 1);
      setStatusCounts(statusCountsRes.data || {});
      setCityCounts(cityCountsRes.data || {});
      setEmployeeCounts(employeeCountsRes.data || {});
      setCardCounts(cardCountsRes.data || {});
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [search, filterEmployee, filterStatus, filterCity, filterSource, filterDateFrom, filterDateTo, activeFilter, currentPage, itemsPerPage, user?.country_id]);

  // Auto-assign unassigned leads on page load (for HR/admin users only)
  const [isAssigning, setIsAssigning] = useState(false);
  
  const assignUnassignedLeads = useCallback(async (showToast = true) => {
    // Only HR managers and admins can trigger bulk assignment
    const allowedRoles = ['HR_MANAGER', 'CEO', 'COUNTRY_HEAD', 'ADMIN', 'HR_ADMIN'];
    if (!user || !allowedRoles.includes(user.role_code)) {
      console.log('User role not allowed for auto-assignment:', user?.role_code);
      return;
    }
    
    setIsAssigning(true);
    try {
      console.log('Triggering auto-assignment for unassigned leads...');
      const response = await leadsApi.assignUnassigned();
      console.log('Auto-assignment response:', response.data);
      
      if (response.data?.assigned_count > 0) {
        if (showToast) {
          toast.success(`${response.data.assigned_count} lead(s) auto-assigned to sales reps`);
        }
        // Refresh the data to show updated assignments
        await fetchData();
      } else if (showToast && response.data?.failed_count > 0) {
        toast.warning(`${response.data.failed_count} lead(s) could not be assigned (no matching sales reps)`);
      }
      return response.data;
    } catch (error) {
      console.error('Auto-assignment error:', error.response?.data || error.message);
      if (showToast) {
        toast.error(error.response?.data?.detail || 'Failed to auto-assign leads');
      }
    } finally {
      setIsAssigning(false);
    }
  }, [user, fetchData]);

  // Initial data fetch
  useEffect(() => { 
    fetchData(); 
  }, [fetchData]);

  // Fetch sales employees for the "Assigned To" dropdown (one-time)
  useEffect(() => {
    employeesApi.getSalesForAssignment().then(res => {
      setSalesEmployeesForForm(res.data || []);
    }).catch(() => {});
  }, []);
  
  // Run auto-assignment after initial data load
  const hasRunAutoAssign = useRef(false);
  
  useEffect(() => {
    // Only run once after initial load completes
    if (!loading && user && !hasRunAutoAssign.current) {
      hasRunAutoAssign.current = true;
      console.log('Page loaded, triggering auto-assignment...');
      assignUnassignedLeads(false); // Silent on initial load
    }
  }, [loading, user, assignUnassignedLeads]);

  // Reset the ref when user changes (logout/login)
  useEffect(() => {
    hasRunAutoAssign.current = false;
  }, [user?.id]);

  // Delete lead (CEO only)
  const [deletingLead, setDeletingLead] = useState(null);
  
  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Are you sure you want to delete "${lead.name}" (${lead.mobile})?\n\nThis will permanently delete:\n- The lead record\n- All notes\n- All activities\n- All reassignment logs\n\nThis action cannot be undone!`)) {
      return;
    }
    
    setDeletingLead(lead.id);
    try {
      const response = await fetch(`/api/leads/${lead.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete lead');
      }
      
      const data = await response.json();
      toast.success(data.message);
      await fetchData(); // Refresh leads list
    } catch (error) {
      toast.error(error.message || 'Failed to delete lead');
    } finally {
      setDeletingLead(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile || !formData.city || (!editingLead && !formData.assigned_to)) {
      toast.error('Please fill in required fields (Name, Mobile, City, Assigned To)');
      return;
    }
    setSaving(true);
    try {
      if (editingLead) {
        await leadsApi.update(editingLead.id, formData);
        toast.success('Lead updated');
      } else {
        await leadsApi.create(formData);
        toast.success('Lead created');
      }
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    // Always assign to current user for new leads
    const defaultAssignedTo = user?.id || '';
    // Get cities for the current user
    const selectedEmp = salesEmployeesForForm.find(e => e.id === defaultAssignedTo);
    const empCities = selectedEmp?.assigned_cities || [];
    const defaultCity = empCities.length === 1 ? empCities[0] : '';
    // Auto-select B2C partner for new leads
    const defaultPartnerId = defaultB2CPartner?.id || '';
    const defaultPartnerName = defaultB2CPartner?.name || '';
    setFormData({ name: '', mobile: '', email: '', city: defaultCity, source: 'WEBSITE', status: 'NEW LEAD',
      assigned_to: defaultAssignedTo, reminder_date: '', reminder_time: '', notes: '', service_type: '', ad_id: '', 
      partner_id: defaultPartnerId, partner_name: defaultPartnerName });
    setEditingLead(null);
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setFormData({ name: lead.name, mobile: lead.mobile, email: lead.email || '', city: lead.city, source: lead.source,
      status: lead.status, assigned_to: lead.assigned_to || '', reminder_date: lead.reminder_date || '',
      reminder_time: lead.reminder_time || '', notes: lead.notes || '', service_type: lead.service_type || '',
      ad_id: lead.ad_id || '', partner_id: lead.partner_id || '', partner_name: lead.partner_name || '' });
    setIsModalOpen(true);
  };

  const openPaymentModal = (lead) => {
    setSelectedLead(lead);
    setPaymentFormData({
      hasCarDetails: 'yes', carNo: '', carMake: '', carModel: '', carYear: '',
      fuelType: '', carColor: '', carConfirmed: false, packageId: '',
      discountType: 'none', discountValue: '', city: lead.city || '',
      customerMobile: lead.mobile, customerName: lead.name,
      customerEmail: lead.email || '',
      selectedOfferIds: [], usePartialPayment: false,
    });
    setCarError('');
    setVehicleData(null);
    setInspectionSchedules([]); // Reset schedules
    setModalStep(1);
    setIsPaymentModalOpen(true);
  };

  const openAssignModal = async (lead) => {
    setAssigningLead(lead);
    setSelectedEmployee(lead.assigned_to || '');
    setLoadingSalesReps(true);
    setSalesRepsForCity([]);
    
    try {
      // Fetch sales reps filtered by lead's city
      const response = await leadsApi.getSalesRepsByCity(lead.city);
      setSalesRepsForCity(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sales reps:', error);
      toast.error('Failed to load sales representatives');
    } finally {
      setLoadingSalesReps(false);
    }
    
    setIsAssignModalOpen(true);
  };

  const handleAssignEmployee = async () => {
    if (!assigningLead || !reassignReason) {
      toast.error('Please provide a reason for reassignment');
      return;
    }
    if (!selectedEmployee) {
      toast.error('Please select a sales representative');
      return;
    }
    setSaving(true);
    try {
      await leadsApi.reassign(assigningLead.id, { new_agent_id: selectedEmployee, reason: reassignReason });
      toast.success('Lead reassigned successfully');
      setIsAssignModalOpen(false);
      setAssigningLead(null);
      setSelectedEmployee('');
      setReassignReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign lead');
    } finally {
      setSaving(false);
    }
  };

  const openReminderModal = (lead) => {
    setReminderLead(lead);
    setReminderFormData({
      reminder_date: lead.reminder_date || '', reminder_time: lead.reminder_time || '',
      reminder_reason: lead.reminder_reason || '', notes: lead.notes || '',
    });
    setIsReminderModalOpen(true);
  };

  const handleSaveReminder = async () => {
    if (!reminderLead || !reminderFormData.reminder_date || !reminderFormData.reminder_time) {
      toast.error('Please select date and time');
      return;
    }
    setSaving(true);
    try {
      // Save reminder using the dedicated reminder endpoint (logs activity)
      await leadsApi.setReminder(reminderLead.id, {
        reminder_date: reminderFormData.reminder_date,
        reminder_time: reminderFormData.reminder_time,
        reminder_reason: reminderFormData.reminder_reason,
      });
      // If notes were changed, update them separately so they get logged
      if (reminderFormData.notes && reminderFormData.notes !== (reminderLead.notes || '')) {
        await leadsApi.update(reminderLead.id, { ...reminderLead, notes: reminderFormData.notes });
      }
      toast.success('Reminder saved successfully');
      setIsReminderModalOpen(false);
      setReminderLead(null);
      setReminderFormData({ reminder_date: '', reminder_time: '', reminder_reason: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save reminder');
    } finally {
      setSaving(false);
    }
  };

  // Check payment status manually (Plan B for when webhooks are delayed)
  const [checkingPayment, setCheckingPayment] = useState({});
  
  const handleCheckPaymentStatus = async (leadId) => {
    setCheckingPayment(prev => ({ ...prev, [leadId]: true }));
    try {
      const response = await leadsApi.checkPaymentStatus(leadId);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        if (data.was_updated) {
          toast.success('🎉 Payment confirmed! Lead has been updated.');
        } else {
          toast.success('Payment already confirmed.');
        }
        fetchData(); // Refresh to show updated status
      } else if (data.payment_status === 'attempted') {
        toast.warning('Customer started payment but did not complete.');
      } else if (data.payment_status === 'expired') {
        toast.error('Payment link has expired. Please send a new one.');
      } else if (data.payment_status === 'cancelled') {
        toast.error('Payment link was cancelled.');
      } else {
        toast.info(data.message || 'Waiting for customer to pay.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check payment status');
    } finally {
      setCheckingPayment(prev => ({ ...prev, [leadId]: false }));
    }
  };

  // Fetch payment links history for a lead
  const handleViewPaymentLinks = async (lead) => {
    setLoadingPaymentLinks(true);
    setIsPaymentLinksModalOpen(true);
    setPaymentLinksData(null);
    try {
      const response = await leadsApi.getPaymentLinks(lead.id);
      setPaymentLinksData(response.data);
    } catch (error) {
      toast.error('Failed to load payment links');
      setIsPaymentLinksModalOpen(false);
    } finally {
      setLoadingPaymentLinks(false);
    }
  };

  // Resend SMS via Razorpay
  const handleResendSms = async () => {
    if (!paymentLinksData?.lead_id) return;
    
    setResendingSms(true);
    try {
      const response = await leadsApi.resendPaymentSms(paymentLinksData.lead_id);
      toast.success(response.data.message || 'SMS resent successfully!');
      // Refresh the payment links data
      const refreshResponse = await leadsApi.getPaymentLinks(paymentLinksData.lead_id);
      setPaymentLinksData(refreshResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend SMS');
    } finally {
      setResendingSms(false);
    }
  };

  // NOTE: Stats calculations moved below - leads already filtered by server

  // Date filter presets
  const applyDatePreset = (preset) => {
    if (preset === 'all' || preset === '') {
      setFilterDateFrom('');
      setFilterDateTo('');
      setDateFilterPreset(preset);
      setCurrentPage(1);
      return;
    }
    
    // Map preset keys to unified component keys
    const presetMap = {
      'today': 'today',
      'yesterday': 'yesterday',
      'last_7_days': 'last_7_days',
      'last_14_days': 'last_14_days',
      'this_week': 'week',
      'last_week': 'last_week',
      'this_month': 'month',
      'last_month': 'last_month',
      'week': 'week',
      'month': 'month',
    };
    
    const mappedPreset = presetMap[preset] || preset;
    const { from, to } = getDateRange(mappedPreset);
    
    setFilterDateFrom(from);
    setFilterDateTo(to);
    setDateFilterPreset(preset);
    setCurrentPage(1);
  };

  // Server-side pagination: leads are already filtered by the backend
  // No client-side filtering needed
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Get selected package details
  const getSelectedPackage = () => {
    return inspectionPackages.find(p => p.id === paymentFormData.packageId);
  };

  // Calculate base amount from selected package (no longer multiplied by numberOfCars)
  const getBaseAmount = () => {
    const pkg = getSelectedPackage();
    if (!pkg) return 0;
    return pkg.price;
  };

  // Calculate discount amount from package settings (if allow_discount is enabled)
  const getDiscountAmount = () => {
    const pkg = getSelectedPackage();
    if (!pkg?.allow_discount) return 0;
    // No discount when partial payment is selected (mutual exclusion at sales level)
    if (paymentFormData.usePartialPayment) return 0;
    
    const baseAmount = getBaseAmount();
    if (paymentFormData.discountValue) {
      let amt = parseFloat(paymentFormData.discountValue);
      // Enforce max discount fixed from package
      const maxFixed = pkg.max_discount_fixed || 0;
      if (maxFixed > 0) amt = Math.min(amt, maxFixed);
      return amt;
    }
    return 0;
  };

  // Calculate offer discount amount from selected offers
  const getOfferDiscountAmount = () => {
    const pkg = getSelectedPackage();
    if (!pkg?.allow_offers || !paymentFormData.selectedOfferIds?.length) return 0;
    
    const baseAmountAfterDiscount = getBaseAmount() - getDiscountAmount();
    let offerDiscount = 0;
    
    paymentFormData.selectedOfferIds.forEach(offerId => {
      const offer = availableOffers.find(o => o.id === offerId);
      if (offer && pkg.applicable_offer_ids?.includes(offerId)) {
        if (offer.discount_type === 'percentage') {
          offerDiscount += baseAmountAfterDiscount * offer.discount_value / 100;
        } else {
          offerDiscount += offer.discount_value;
        }
      }
    });
    
    return offerDiscount;
  };

  // Get offers applicable to the selected package
  const getApplicableOffers = () => {
    const pkg = getSelectedPackage();
    if (!pkg?.allow_offers || !pkg?.applicable_offer_ids?.length) return [];
    return availableOffers.filter(o => pkg.applicable_offer_ids.includes(o.id));
  };

  // Calculate final amount after discount and offers (this is the TOTAL amount customer needs to pay)
  const calculateFinalAmount = () => {
    const baseAmount = getBaseAmount();
    const discount = getDiscountAmount();
    const offerDiscount = getOfferDiscountAmount();
    return Math.max(0, baseAmount - discount - offerDiscount);
  };

  // Get partial payment amount (fixed amount from package settings)
  // This is what customer pays IMMEDIATELY
  const getPartialPaymentAmount = () => {
    const pkg = getSelectedPackage();
    if (!pkg?.allow_partial_payment || !paymentFormData.usePartialPayment) return 0;
    
    const finalAmount = calculateFinalAmount();
    // Partial payment is a fixed amount, but cannot exceed total
    return Math.min(pkg.partial_payment_value || 0, finalAmount);
  };

  // Get remaining balance amount (to be collected later via Collect Balance button)
  const getRemainingBalance = () => {
    const pkg = getSelectedPackage();
    if (!pkg?.allow_partial_payment || !paymentFormData.usePartialPayment) return 0;
    return calculateFinalAmount() - getPartialPaymentAmount();
  };

  // Get the amount to charge now (either full amount or partial payment)
  const getAmountToPayNow = () => {
    const pkg = getSelectedPackage();
    if (pkg?.allow_partial_payment && paymentFormData.usePartialPayment) {
      return getPartialPaymentAmount();
    }
    return calculateFinalAmount();
  };

  // Handle package selection and initialize schedules
  const handlePackageSelect = (packageId) => {
    // Reset discount and offers when package changes
    setPaymentFormData(prev => ({ 
      ...prev, 
      packageId,
      discountType: 'none',
      discountValue: '',
      selectedOfferIds: [],
      usePartialPayment: false,
    }));
    if (packageId && packageId !== 'select') {
      const pkg = inspectionPackages.find(p => p.id === packageId);
      if (pkg) {
        initializeInspectionSchedules(pkg);
      }
    } else {
      setInspectionSchedules([]);
    }
  };

  return (
    <div className="p-5 max-w-[1600px] mx-auto" data-testid="leads-page">
      {/* Full Page Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
            <p className="text-sm font-medium text-gray-600">Loading leads...</p>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500 mt-1">Manage and track all your sales leads</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { resetForm(); setIsModalOpen(true); }}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 flex items-center gap-2 font-medium shadow-lg shadow-blue-500/25 transition-all"
            data-testid="add-lead-button"
          >
            <Plus className="h-4 w-4" /> Add Lead
          </button>
        </div>
      </div>

      {/* Date Range Filter - Above Stats Cards */}
      <div className="bg-white rounded-xl border p-4 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-600">Date Range:</span>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: 'last_7_days', label: 'Last 7 Days' },
              { key: 'last_14_days', label: 'Last 14 Days' },
              { key: 'this_week', label: 'This Week' },
              { key: 'this_month', label: 'This Month' },
              { key: 'custom', label: 'Custom' },
            ].map((preset) => (
              <button 
                key={preset.key} 
                onClick={() => applyDatePreset(preset.key)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  dateFilterPreset === preset.key 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`date-preset-${preset.key}`}
              >
                {preset.label}
              </button>
            ))}
            <button 
              onClick={() => applyDatePreset('all')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                dateFilterPreset === 'all' || dateFilterPreset === ''
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="date-preset-all"
            >
              All Time
            </button>
          </div>
          {dateFilterPreset === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => { setFilterDateFrom(e.target.value); setCurrentPage(1); }}
                className="h-9 px-3 border rounded-lg text-sm"
                data-testid="date-from-input"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => { setFilterDateTo(e.target.value); setCurrentPage(1); }}
                className="h-9 px-3 border rounded-lg text-sm"
                data-testid="date-to-input"
              />
            </div>
          )}
        </div>
      </div>

      {/* Sales Agent Dashboard - Action-oriented Summary Cards */}
      <LeadStats 
        cardCounts={cardCounts}
        activeFilter={activeFilter}
        onFilterChange={(filter) => { setActiveFilter(filter); setCurrentPage(1); }}
      />

      {/* Filters Section - Using LeadFilters component */}
      <LeadFilters
        search={search}
        setSearch={(v) => { setSearch(v); setCurrentPage(1); }}
        filterEmployee={filterEmployee}
        setFilterEmployee={(v) => { setFilterEmployee(v); setCurrentPage(1); }}
        filterStatus={filterStatus}
        setFilterStatus={(v) => { setFilterStatus(v); setCurrentPage(1); }}
        filterCity={filterCity}
        setFilterCity={(v) => { setFilterCity(v); setCurrentPage(1); }}
        employees={filteredEmployees}
        employeeCounts={employeeCounts}
        statuses={statuses}
        statusCounts={statusCounts}
        cities={filteredCities}
        cityCounts={cityCounts}
        onReset={() => {
          setActiveFilter('all');
          setSearch('');
          setFilterEmployee('');
          setFilterStatus('');
          setFilterCity('');
          setFilterDateFrom('');
          setFilterDateTo('');
          setDateFilterPreset('');
          setCurrentPage(1);
        }}
        isSalesExec={isSalesExec}
        userName={user?.name}
      />

      {/* Data Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead>
            <tr className="bg-slate-50 border-b">
              <th className="w-[90px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
              <th className="w-[220px] min-w-[220px] max-w-[220px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Lead Details</th>
              <th className="w-[90px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">City</th>
              <th className="w-[120px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Assigned</th>
              <th className="w-[90px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Reminder</th>
              <th className="w-[110px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
              <th className="w-[130px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Payment</th>
              <th className="w-[70px] px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="text-gray-500">Loading leads...</span>
                  </div>
                </td>
              </tr>
            ) : leads.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No leads found</p>
                </td>
              </tr>
            ) : (
              leads.map((lead) => (
                <tr key={lead.id} className="hover:bg-slate-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{formatDate(lead.created_at)}</div>
                    <div className="text-xs text-gray-400">{formatTime(lead.created_at)}</div>
                  </td>
                  <td className="px-4 py-3 w-[220px] min-w-[220px] max-w-[220px]">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                        {lead.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="flex items-center gap-1">
                          <span 
                            className="font-medium text-gray-900 text-sm truncate max-w-[120px] block cursor-default" 
                            title={lead.name}
                          >
                            {lead.name}
                          </span>
                          <button 
                            onClick={() => openEditModal(lead)} 
                            className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                            data-testid={`edit-lead-${lead.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          {/* Delete button - CEO only */}
                          {user?.role_code === 'CEO' && (
                            <button 
                              onClick={() => handleDeleteLead(lead)}
                              disabled={deletingLead === lead.id}
                              className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 flex-shrink-0"
                              data-testid={`delete-lead-${lead.id}`}
                              title="Delete lead (CEO only)"
                            >
                              {deletingLead === lead.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 font-mono flex items-center gap-2">
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {lead.mobile}
                          </span>
                          {lead.email && (
                            <span className="flex items-center gap-1 text-blue-600" title={lead.email}>
                              <Mail className="h-3 w-3" /> ✓
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                      <MapPin className="h-3 w-3 text-gray-400" />
                      {lead.city || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-700 truncate max-w-[90px]" title={lead.assigned_to_name || lead.assigned_to}>
                        {lead.assigned_to_name || lead.assigned_to || '-'}
                      </span>
                      {/* Hide reassign button for Sales Executives */}
                      {!isSalesExec && (
                        <button 
                          onClick={() => openAssignModal(lead)}
                          className="p-0.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                          data-testid={`assign-employee-${lead.id}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <button 
                        className="px-2.5 py-1 text-xs bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 font-medium flex items-center gap-1 shadow-sm"
                        onClick={() => openReminderModal(lead)}
                        data-testid={`add-reminder-${lead.id}`}
                      >
                        <Bell className="h-3 w-3" />
                        {lead.reminder_date ? 'Edit' : 'Add'}
                      </button>
                      {lead.reminder_date && (
                        <div className="text-xs bg-slate-50 p-1.5 rounded border">
                          <div className="flex items-center gap-1 text-gray-600 font-medium">
                            <Clock className="h-3 w-3" />
                            {formatDate(lead.reminder_date)}{lead.reminder_time ? `, ${lead.reminder_time}` : ''}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {/* Clickable Status Dropdown */}
                    <StatusDropdown lead={lead} statuses={statuses} onUpdate={fetchData} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      {(lead.payment_status === 'paid' || lead.status === 'PAID') && (
                        <div className="flex items-center gap-1 text-emerald-600 mb-1">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-xs font-medium">Paid</span>
                          {lead.payment_amount && (
                            <span className="text-xs text-gray-500">(₹{lead.payment_amount?.toLocaleString()})</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <button 
                          className={`px-2.5 py-1 text-xs ${
                            lead.payment_status === 'paid' || lead.status === 'PAID'
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                              : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                          } text-white rounded-lg font-medium flex items-center gap-1 shadow-sm`}
                          onClick={() => openPaymentModal(lead)}
                          data-testid={`send-pay-link-${lead.id}`}
                        >
                          <CreditCard className="h-3 w-3" />
                          {lead.payment_status === 'paid' || lead.status === 'PAID' 
                            ? 'Send (Again)' 
                            : lead.payment_link ? 'Resend' : 'Send'}
                        </button>
                        <button
                          onClick={() => handleViewPaymentLinks(lead)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View payment link history"
                          data-testid={`payment-links-info-${lead.id}`}
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </div>
                      {lead.payment_link && (
                        <div className="flex items-center gap-1 flex-wrap">
                          <button
                            onClick={() => window.open(lead.payment_link, '_blank')}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5"
                            data-testid={`view-link-${lead.id}`}
                          >
                            <Eye className="h-3 w-3" /> View
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={async () => {
                              try { await navigator.clipboard.writeText(lead.payment_link); toast.success('Link copied!'); }
                              catch (err) { toast.success('Link copied!'); }
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700 font-medium flex items-center gap-0.5"
                            data-testid={`copy-link-${lead.id}`}
                          >
                            <Copy className="h-3 w-3" /> Copy
                          </button>
                          {!(lead.payment_status === 'paid' || lead.status === 'PAID') && (
                            <>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => handleCheckPaymentStatus(lead.id)}
                                disabled={checkingPayment[lead.id]}
                                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-0.5 disabled:opacity-50"
                                data-testid={`check-payment-${lead.id}`}
                              >
                                {checkingPayment[lead.id] ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3 w-3" />
                                )}
                                Check
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {/* Notes Button */}
                    <button 
                      className="px-2.5 py-1 text-xs bg-gradient-to-r from-slate-500 to-slate-600 text-white rounded-lg hover:from-slate-600 hover:to-slate-700 font-medium flex items-center gap-1 shadow-sm"
                      onClick={() => openNotesDrawer(lead)}
                      data-testid={`view-notes-${lead.id}`}
                    >
                      <StickyNote className="h-3 w-3" />
                      Notes
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        
        {/* Pagination */}
        {!loading && totalLeads > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50" data-testid="leads-pagination">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{startIndex + 1}</span> to <span className="font-medium">{Math.min(startIndex + leads.length, totalLeads)}</span> of <span className="font-medium">{totalLeads}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                data-testid="leads-prev-page"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 border rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-1">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum = totalPages <= 5 ? i + 1 : currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      data-testid={`leads-page-${pageNum}`}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                        currentPage === pageNum ? 'bg-blue-600 text-white' : 'hover:bg-white border'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="leads-next-page"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 border rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Lead Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="lead-modal">
          <DialogHeader className="border-b pb-4 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              {editingLead ? 'Edit Lead' : 'Add New Lead'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Lead Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="h-10" data-testid="lead-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Lead Source</Label>
                <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                  <SelectTrigger className="h-10" data-testid="lead-source-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{sources.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Mobile Number *</Label>
                <Input value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} className="h-10 font-mono" data-testid="lead-mobile-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email ID</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                  className="h-10" 
                  placeholder="customer@email.com (used for Razorpay & Meta)" 
                  data-testid="lead-email-input" 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Ad ID (from Settings)</Label>
                <Input value={formData.ad_id} onChange={(e) => setFormData({ ...formData, ad_id: e.target.value })} className="h-10 font-mono" placeholder="Optional" data-testid="lead-ad-id-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">City *</Label>
                {(() => {
                  // For new leads: filter cities based on current user's assigned cities
                  // For editing: filter based on selected assigned_to employee
                  const empId = editingLead ? formData.assigned_to : user?.id;
                  const selectedEmp = salesEmployeesForForm.find(e => e.id === empId);
                  const empCities = selectedEmp?.assigned_cities || [];
                  const cityOptions = empCities.length > 0 ? empCities : cities;
                  return (
                    <Select value={formData.city} onValueChange={(v) => setFormData({ ...formData, city: v })}>
                      <SelectTrigger className="h-10" data-testid="lead-city-select"><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent>{cityOptions.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Lead Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="h-10" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {statuses.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assigned To *</Label>
                {editingLead ? (
                  <Select 
                    value={formData.assigned_to} 
                    onValueChange={(v) => {
                      const emp = salesEmployeesForForm.find(e => e.id === v);
                      const empCities = emp?.assigned_cities || [];
                      const newCity = empCities.includes(formData.city) ? formData.city : (empCities.length === 1 ? empCities[0] : '');
                      setFormData({ ...formData, assigned_to: v, city: newCity });
                    }}
                  >
                    <SelectTrigger className="h-10" data-testid="lead-assigned-to-select"><SelectValue placeholder="Select sales executive" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {salesEmployeesForForm.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input 
                    value={user?.name || ''} 
                    readOnly 
                    className="h-10 bg-gray-50 cursor-not-allowed" 
                    data-testid="lead-assigned-to-display" 
                  />
                )}
              </div>
            </div>
            {/* Partner/Client Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Partner/Client</Label>
              <Select 
                value={formData.partner_id || ''} 
                onValueChange={(v) => {
                  const selectedPartner = partners.find(p => p.id === v);
                  setFormData({ 
                    ...formData, 
                    partner_id: v, 
                    partner_name: selectedPartner?.name || '' 
                  });
                }}
              >
                <SelectTrigger className="h-10" data-testid="lead-partner-select">
                  <SelectValue placeholder="Select partner (default: B2C)" />
                </SelectTrigger>
                <SelectContent>
                  {partners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.type.toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Leave empty for default B2C customer</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Notes</Label>
              <textarea 
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full min-h-[80px] px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter notes..."
                data-testid="lead-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-4 border-t mt-2 sticky bottom-0 bg-white pb-1">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800" data-testid="update-lead-button">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingLead ? 'Update Lead' : 'Create Lead'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Modal with Discount and Inspection Schedule */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="sm:max-w-[750px] p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="payment-modal">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4">
            <DialogTitle className="text-lg font-semibold text-white">Send Payment Link</DialogTitle>
            <div className="text-sm mt-1 text-blue-100">
              Customer: {paymentFormData.customerName} | Mobile: {paymentFormData.customerMobile}
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {/* Step 1: Car Details */}
              {modalStep === 1 && (
                <>
                  <div className="text-lg font-semibold text-gray-900 pb-2 border-b">Step 1: Car Details</div>
                  <div className="flex items-center gap-6 py-2">
                    <Label className="text-sm font-medium">Does the customer know car details?</Label>
                    <div className="flex gap-4">
                      {['yes', 'no'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input type="radio" name="hasCarDetails" value={opt} checked={paymentFormData.hasCarDetails === opt}
                            onChange={() => setPaymentFormData({ ...paymentFormData, hasCarDetails: opt, carConfirmed: false })}
                            className="w-4 h-4 text-blue-600" />
                          <span className="text-sm capitalize">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {paymentFormData.hasCarDetails === 'yes' && (
                    <div className="space-y-4">
                      <div className="flex items-end gap-4">
                        <div className="flex-1 space-y-2">
                          <Label className="text-sm">Car Registration Number</Label>
                          <Input value={paymentFormData.carNo} onChange={(e) => setPaymentFormData({ ...paymentFormData, carNo: e.target.value.toUpperCase() })}
                            className="h-11 font-mono uppercase" placeholder="KA01AB1234" data-testid="car-number-input" />
                        </div>
                        <Button onClick={() => fetchCarDetails(paymentFormData.carNo)} disabled={!paymentFormData.carNo || carLoading}
                          className="h-11 px-6 bg-gradient-to-r from-amber-500 to-amber-600" data-testid="get-car-data-btn">
                          {carLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Get Car Data
                        </Button>
                      </div>
                      {paymentFormData.carMake && (
                        <div className="bg-emerald-50 p-5 rounded-xl border border-emerald-200">
                          <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full" /> Vehicle Information (Vaahan API)
                          </h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div><span className="text-gray-500">Make:</span> <span className="font-medium ml-2">{paymentFormData.carMake}</span></div>
                            <div><span className="text-gray-500">Model:</span> <span className="font-medium ml-2">{paymentFormData.carModel}</span></div>
                            <div><span className="text-gray-500">Year:</span> <span className="font-medium ml-2">{paymentFormData.carYear}</span></div>
                            <div><span className="text-gray-500">Fuel:</span> <span className="font-medium ml-2">{paymentFormData.fuelType}</span></div>
                            <div><span className="text-gray-500">Color:</span> <span className="font-medium ml-2">{paymentFormData.carColor}</span></div>
                            <div><span className="text-gray-500">Owner:</span> <span className="font-medium ml-2">{paymentFormData.ownerName}</span></div>
                            {paymentFormData.registrationDate && (
                              <div><span className="text-gray-500">Reg Date:</span> <span className="font-medium ml-2">{paymentFormData.registrationDate}</span></div>
                            )}
                            {paymentFormData.rcExpiryDate && (
                              <div><span className="text-gray-500">RC Expiry:</span> <span className="font-medium ml-2">{paymentFormData.rcExpiryDate}</span></div>
                            )}
                            {paymentFormData.insuranceCompany && (
                              <div className="col-span-2"><span className="text-gray-500">Insurance:</span> <span className="font-medium ml-2">{paymentFormData.insuranceCompany} (Valid: {paymentFormData.insuranceValidUpto})</span></div>
                            )}
                          </div>
                          <label className="flex items-center gap-3 mt-4 pt-4 border-t border-emerald-200 cursor-pointer">
                            <input type="checkbox" checked={paymentFormData.carConfirmed} onChange={(e) => setPaymentFormData({ ...paymentFormData, carConfirmed: e.target.checked })}
                              className="w-5 h-5 text-emerald-600 rounded" />
                            <span className="text-sm font-medium">Customer confirms these details are correct</span>
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Step 2: Package, Discount & Inspection Schedule */}
              {modalStep === 2 && (
                <>
                  <div className="text-lg font-semibold text-gray-900 pb-2 border-b">Step 2: Package & Pricing</div>

                  {/* Email capture — soft enforcement */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Customer Email {!paymentFormData.customerEmail && <span className="text-amber-600 font-normal">(recommended for payment receipt)</span>}</Label>
                    <Input
                      type="email"
                      value={paymentFormData.customerEmail}
                      onChange={e => setPaymentFormData({ ...paymentFormData, customerEmail: e.target.value })}
                      placeholder="customer@email.com"
                      className={`h-10 ${!paymentFormData.customerEmail ? 'border-amber-300 bg-amber-50/50' : ''}`}
                      data-testid="payment-email-input"
                    />
                    {!paymentFormData.customerEmail && (
                      <p className="text-[11px] text-amber-600 flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3.5 h-3.5 shrink-0"><path fillRule="evenodd" d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" /></svg>
                        Ask the customer for their email — improves payment receipts & ad performance tracking
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">Select Package *</Label>
                    <Select value={paymentFormData.packageId || 'select'} onValueChange={(v) => handlePackageSelect(v === 'select' ? '' : v)}>
                      <SelectTrigger className="h-11" data-testid="package-select"><SelectValue placeholder="Select Package" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="select">-- Select Package --</SelectItem>
                        {inspectionPackages.map(pkg => (
                          <SelectItem key={pkg.id} value={pkg.id}>
                            {pkg.name} - {pkg.no_of_inspections || 1} Inspection{(pkg.no_of_inspections || 1) > 1 ? 's' : ''} - ₹{pkg.price?.toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Package Details */}
                  {getSelectedPackage() && (
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold text-blue-800">{getSelectedPackage().name}</h4>
                          <p className="text-sm text-blue-600">{getSelectedPackage().no_of_inspections || 1} Inspection(s) included</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-blue-600">Package Price</p>
                          <p className="text-xl font-bold text-blue-800">₹{getSelectedPackage().price?.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Partial Payment Section - Shows when package allows it */}
                  {getSelectedPackage()?.allow_partial_payment && (
                    <div className={`p-4 rounded-xl border ${paymentFormData.usePartialPayment ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" /> Partial Payment
                        </h4>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox 
                            checked={paymentFormData.usePartialPayment}
                            onCheckedChange={(checked) => setPaymentFormData({ 
                              ...paymentFormData, 
                              usePartialPayment: checked,
                              ...(checked ? { discountValue: '' } : {})
                            })}
                            disabled={paymentFormData.discountValue && parseFloat(paymentFormData.discountValue) > 0}
                            data-testid="partial-payment-checkbox"
                          />
                          <span className="text-sm text-purple-700">Enable partial payment</span>
                        </label>
                      </div>
                      {paymentFormData.discountValue && parseFloat(paymentFormData.discountValue) > 0 && !paymentFormData.usePartialPayment && (
                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded mb-2">Partial payment is disabled when discount is applied. Remove discount first.</p>
                      )}
                      <p className="text-xs text-purple-600 mb-2">
                        Partial amount: <span className="font-bold">₹{(getSelectedPackage().partial_payment_value || 0).toLocaleString()}</span>
                      </p>
                      {paymentFormData.usePartialPayment && (
                        <div className="bg-purple-100/50 p-3 rounded-lg space-y-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-purple-700">Total amount:</span>
                            <span className="font-medium text-purple-800">₹{getBaseAmount().toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-purple-700">Partial payment (now):</span>
                            <span className="font-bold text-purple-800">₹{getPartialPaymentAmount().toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm pt-2 border-t border-purple-300">
                            <span className="text-purple-700 font-medium">Balance (collect later):</span>
                            <span className="font-bold text-purple-900">₹{getRemainingBalance().toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Discount Section - Only show if package allows discount AND partial payment is NOT selected */}
                  {getSelectedPackage()?.allow_discount && (
                    <div className={`p-4 rounded-xl border ${!paymentFormData.usePartialPayment ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                      <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                        <Percent className="h-4 w-4" /> Discount (Optional)
                        {getSelectedPackage()?.max_discount_fixed > 0 && (
                          <span className="text-xs font-normal text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                            Max: ₹{getSelectedPackage().max_discount_fixed}
                          </span>
                        )}
                      </h4>
                      {paymentFormData.usePartialPayment ? (
                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Discount is disabled when partial payment is selected. Uncheck partial payment to apply discount.</p>
                      ) : (
                        <div className="space-y-2">
                          <Label className="text-sm">Discount Amount (₹)</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 font-medium">₹</span>
                            <Input 
                              type="number" 
                              value={paymentFormData.discountValue} 
                              onChange={(e) => {
                                let val = e.target.value;
                                const pkg = getSelectedPackage();
                                const maxFixed = pkg?.max_discount_fixed || 0;
                                if (maxFixed > 0 && parseFloat(val) > maxFixed) val = String(maxFixed);
                                setPaymentFormData({ ...paymentFormData, discountValue: val });
                              }}
                              className="h-10 max-w-[200px]" 
                              placeholder="e.g., 100"
                              data-testid="discount-value"
                            />
                          </div>
                          {getSelectedPackage()?.max_discount_fixed > 0 && (
                            <p className="text-xs text-amber-600">Maximum allowed: ₹{getSelectedPackage().max_discount_fixed}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Offers Section - Only show if package allows offers and has applicable offers */}
                  {getSelectedPackage()?.allow_offers && getApplicableOffers().length > 0 && (
                    <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                      <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                        <Gift className="h-4 w-4" /> Apply Offers
                      </h4>
                      <p className="text-sm text-orange-600 mb-3">Select offers to apply (multiple can be selected)</p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {getApplicableOffers().map((offer) => (
                          <label 
                            key={offer.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              paymentFormData.selectedOfferIds?.includes(offer.id) 
                                ? 'bg-orange-100 border-2 border-orange-400' 
                                : 'bg-white border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <Checkbox 
                              checked={paymentFormData.selectedOfferIds?.includes(offer.id)}
                              onCheckedChange={(checked) => {
                                const current = paymentFormData.selectedOfferIds || [];
                                if (checked) {
                                  setPaymentFormData({ ...paymentFormData, selectedOfferIds: [...current, offer.id] });
                                } else {
                                  setPaymentFormData({ ...paymentFormData, selectedOfferIds: current.filter(id => id !== offer.id) });
                                }
                              }}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{offer.name}</p>
                              <p className="text-xs text-gray-500">
                                {offer.discount_type === 'percentage' ? `${offer.discount_value}% off` : `₹${offer.discount_value} off`}
                                {' • '}Valid until {new Date(offer.valid_until).toLocaleDateString()}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Price Summary Section */}
                  {paymentFormData.packageId && (
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-3">Payment Summary</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Package Price:</span>
                          <span className="text-gray-800 font-medium">₹{getBaseAmount().toLocaleString()}</span>
                        </div>
                        {getDiscountAmount() > 0 && (
                          <div className="flex justify-between items-center text-sm text-green-700">
                            <span>Discount ({paymentFormData.discountType === 'percent' ? `${paymentFormData.discountValue}%` : `₹${paymentFormData.discountValue}`}):</span>
                            <span>- ₹{getDiscountAmount().toLocaleString()}</span>
                          </div>
                        )}
                        {getOfferDiscountAmount() > 0 && (
                          <div className="flex justify-between items-center text-sm text-orange-700">
                            <span>Offer Discount ({paymentFormData.selectedOfferIds?.length} offer{paymentFormData.selectedOfferIds?.length > 1 ? 's' : ''}):</span>
                            <span>- ₹{getOfferDiscountAmount().toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                          <span className="text-gray-800 font-semibold">Total Amount:</span>
                          <span className={`font-bold ${getSelectedPackage()?.allow_partial_payment && paymentFormData.usePartialPayment ? 'text-gray-500 line-through text-base' : 'text-xl text-gray-800'}`}>
                            ₹{calculateFinalAmount().toLocaleString()}
                          </span>
                        </div>
                        {getSelectedPackage()?.allow_partial_payment && paymentFormData.usePartialPayment && (
                          <div className="bg-purple-100 p-3 rounded-lg mt-2 space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-purple-800 font-semibold">Pay Now (Partial):</span>
                              <span className="font-bold text-xl text-purple-800">₹{getPartialPaymentAmount().toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-purple-600">
                              <span>Balance (Collect Later):</span>
                              <span className="font-medium">₹{getRemainingBalance().toLocaleString()}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Inspection Schedule Section - Only show if car details were provided in Step 1 */}
                  {paymentFormData.hasCarDetails === 'yes' && paymentFormData.carConfirmed && inspectionSchedules.length > 0 && (
                    <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                      <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                        <CalendarDays className="h-4 w-4" /> Inspection Schedule (Optional)
                      </h4>
                      <p className="text-sm text-emerald-600 mb-4">
                        You can schedule inspections now or leave them for later. Unscheduled inspections will appear in the Inspections tab.
                      </p>
                      
                      <div className="space-y-6">
                        {inspectionSchedules.map((schedule, index) => (
                          <div key={schedule.id} className="bg-white p-4 rounded-lg border border-emerald-200">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-emerald-800 flex items-center gap-2">
                                <Car className="h-4 w-4" />
                                Inspection {index + 1} of {inspectionSchedules.length}
                              </h5>
                              {schedule.vehicleConfirmed && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                                  Vehicle Confirmed
                                </span>
                              )}
                            </div>

                            {/* Vehicle Number Input with Vaahan Lookup */}
                            <div className="space-y-3">
                              <div className="flex items-end gap-3">
                                <div className="flex-1 space-y-1">
                                  <Label className="text-xs text-gray-600">Vehicle Registration Number</Label>
                                  <Input
                                    value={schedule.vehicleNumber}
                                    onChange={(e) => updateSchedule(index, 'vehicleNumber', e.target.value.toUpperCase())}
                                    className="h-10 font-mono uppercase"
                                    placeholder="KA01AB1234"
                                    disabled={schedule.vehicleConfirmed}
                                    data-testid={`schedule-${index}-vehicle`}
                                  />
                                </div>
                                {!schedule.vehicleConfirmed && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => fetchVehicleForSchedule(index, schedule.vehicleNumber)}
                                    disabled={!schedule.vehicleNumber || schedule.isLoading}
                                    className="h-10 bg-emerald-600 hover:bg-emerald-700"
                                  >
                                    {schedule.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Get Details'}
                                  </Button>
                                )}
                              </div>

                              {/* Vehicle Details Display */}
                              {schedule.vehicleData && (
                                <div className="bg-emerald-50 p-3 rounded-lg text-sm">
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div><span className="text-gray-500">Make:</span> <span className="font-medium">{schedule.vehicleData.manufacturer}</span></div>
                                    <div><span className="text-gray-500">Model:</span> <span className="font-medium">{schedule.vehicleData.model}</span></div>
                                    <div><span className="text-gray-500">Color:</span> <span className="font-medium">{schedule.vehicleData.color}</span></div>
                                  </div>
                                  {!schedule.vehicleConfirmed && (
                                    <label className="flex items-center gap-2 mt-3 pt-2 border-t border-emerald-200 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={schedule.vehicleConfirmed}
                                        onChange={(e) => updateSchedule(index, 'vehicleConfirmed', e.target.checked)}
                                        className="w-4 h-4 text-emerald-600 rounded"
                                      />
                                      <span className="text-xs font-medium">Confirm vehicle details</span>
                                    </label>
                                  )}
                                </div>
                              )}

                              {schedule.error && (
                                <p className="text-xs text-red-600">{schedule.error}</p>
                              )}

                              {/* Date, Time, and Address */}
                              <div className="grid grid-cols-2 gap-3 mt-3">
                                <div className="space-y-1">
                                  <Label className="text-xs text-gray-600">Preferred Date</Label>
                                  <Input
                                    type="date"
                                    value={schedule.inspectionDate}
                                    onChange={(e) => updateSchedule(index, 'inspectionDate', e.target.value)}
                                    className="h-9"
                                    min={getLocalDate()}
                                    data-testid={`schedule-${index}-date`}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-gray-600">Preferred Time</Label>
                                  <Select 
                                    value={schedule.inspectionTime || 'select'} 
                                    onValueChange={(v) => updateSchedule(index, 'inspectionTime', v === 'select' ? '' : v)}
                                  >
                                    <SelectTrigger className="h-9" data-testid={`schedule-${index}-time`}>
                                      <SelectValue placeholder="Select Time" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="select">-- Select --</SelectItem>
                                      {['09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM'].map(t => 
                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                      )}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              
                              <div className="space-y-1">
                                <Label className="text-xs text-gray-600">Inspection Address</Label>
                                <PlacesAutocomplete
                                  value={schedule.address}
                                  onChange={(value) => updateSchedule(index, 'address', value)}
                                  onSelect={(place) => {
                                    updateSchedule(index, 'address', place.address);
                                    updateSchedule(index, 'latitude', place.latitude);
                                    updateSchedule(index, 'longitude', place.longitude);
                                  }}
                                  placeholder="Start typing address..."
                                  className="h-9"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Info message when no car details in Step 1 */}
                  {paymentFormData.hasCarDetails === 'no' && getSelectedPackage() && (
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <p className="text-sm text-slate-600 flex items-center gap-2">
                        <CalendarDays className="h-4 w-4" />
                        Inspection scheduling will be available after payment. The {getSelectedPackage().no_of_inspections || 1} inspection(s) will appear in the Inspections tab as "Unscheduled".
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex justify-between items-center p-6 bg-slate-50 border-t">
            <Button variant="outline" onClick={() => modalStep > 1 ? setModalStep(modalStep - 1) : setIsPaymentModalOpen(false)}>
              {modalStep === 1 ? 'Cancel' : 'Back'}
            </Button>
            {modalStep < 2 ? (
              <Button onClick={() => setModalStep(2)} disabled={paymentFormData.hasCarDetails === 'yes' && !paymentFormData.carConfirmed}
                className="bg-gradient-to-r from-amber-500 to-amber-600">Next Step</Button>
            ) : (
              <Button onClick={async () => {
                if (!selectedLead || !paymentFormData.packageId) {
                  toast.error('Please select a package');
                  return;
                }
                const selectedPackage = getSelectedPackage();
                if (!selectedPackage) {
                  toast.error('Invalid package selected');
                  return;
                }
                setSaving(true);
                try {
                  // Save email to lead if provided (for CAPI and Razorpay receipt)
                  if (paymentFormData.customerEmail && paymentFormData.customerEmail !== selectedLead.email) {
                    await leadsApi.update(selectedLead.id, { email: paymentFormData.customerEmail });
                  }
                  
                  // Save vehicle to vehicle master if we have vehicle data
                  if (vehicleData) {
                    await saveVehicleToMaster(selectedLead.id);
                  }
                  
                  // Calculate final amount after discount
                  const finalAmount = calculateFinalAmount();
                  
                  // Prepare inspection schedules data for backend
                  const schedulesData = inspectionSchedules.map((schedule, index) => ({
                    vehicle_number: schedule.vehicleNumber || '',
                    vehicle_data: schedule.vehicleData || null,
                    inspection_date: schedule.inspectionDate || '',
                    inspection_time: schedule.inspectionTime || '',
                    address: schedule.address || '',
                    latitude: schedule.latitude || null,
                    longitude: schedule.longitude || null,
                    slot_number: index + 1,
                  }));
                  
                  // Determine if partial payment is being used
                  const isPartialPayment = selectedPackage.allow_partial_payment && paymentFormData.usePartialPayment;
                  const amountToPayNow = getAmountToPayNow();
                  const balanceDue = isPartialPayment ? getRemainingBalance() : 0;
                  
                  // Use the real Razorpay payment link API - send amount to pay NOW (partial or full)
                  const response = await leadsApi.createPaymentLink(selectedLead.id, {
                    package_id: paymentFormData.packageId,
                    amount: amountToPayNow, // Amount to charge via Razorpay
                    total_amount: finalAmount, // Total package amount after discounts
                    description: `${selectedPackage.name} - ${selectedPackage.no_of_inspections || 1} Inspection(s)${getDiscountAmount() > 0 ? ` (Discount Applied)` : ''}${isPartialPayment ? ' (Partial Payment)' : ''}`,
                    send_via_whatsapp: true,
                    vehicle_number: paymentFormData.carNo || '',
                    no_of_inspections: selectedPackage.no_of_inspections || 1,
                    discount_type: paymentFormData.discountType !== 'none' ? paymentFormData.discountType : null,
                    discount_value: paymentFormData.discountValue || null,
                    base_amount: getBaseAmount(),
                    discount_amount: getDiscountAmount(),
                    inspection_schedules: schedulesData,
                    // Partial payment fields
                    is_partial_payment: isPartialPayment,
                    partial_payment_amount: isPartialPayment ? amountToPayNow : null,
                    balance_due: balanceDue,
                  });
                  
                  // Build success message based on what was sent
                  const channels = [];
                  if (response.data?.sms_sent_by_razorpay) channels.push('SMS (via Razorpay)');
                  if (response.data?.whatsapp_sent) channels.push('WhatsApp');
                  if (response.data?.email_sent) channels.push('Email');
                  
                  let message = channels.length > 0 
                    ? `Payment link sent via ${channels.join(' & ')}!`
                    : 'Payment link generated successfully!';
                  
                  // Show warning if WhatsApp failed but SMS was sent
                  if (response.data?.sms_sent_by_razorpay && response.data?.whatsapp_error) {
                    message = 'Payment link SMS sent by Razorpay. WhatsApp delivery failed.';
                  }
                  
                  toast.success(message);
                  
                  setIsPaymentModalOpen(false);
                  setVehicleData(null); // Clear vehicle data
                  setInspectionSchedules([]); // Clear schedules
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to create payment link');
                } finally { 
                  setSaving(false); 
                }
              }} disabled={saving || !paymentFormData.packageId} className="bg-gradient-to-r from-blue-600 to-blue-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} 
                Send Payment Link (₹{getAmountToPayNow().toLocaleString()})
                {getSelectedPackage()?.allow_partial_payment && paymentFormData.usePartialPayment && (
                  <span className="ml-1 text-xs opacity-75">Partial</span>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Links History Modal */}
      <Dialog open={isPaymentLinksModalOpen} onOpenChange={setIsPaymentLinksModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="payment-links-modal">
          <DialogHeader className="border-b pb-4 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Payment Links History
            </DialogTitle>
            {paymentLinksData && (
              <p className="text-sm text-gray-500 mt-1">
                {paymentLinksData.lead_name} • {paymentLinksData.lead_mobile}
              </p>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingPaymentLinks ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-500">Loading payment links...</span>
              </div>
            ) : !paymentLinksData || paymentLinksData.payment_links?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <CreditCard className="h-10 w-10 mb-2 text-gray-300" />
                <p className="text-sm">No payment links have been sent yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Razorpay SMS Status Card */}
                {paymentLinksData.razorpay_info && (
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-900">Razorpay SMS Notification</span>
                      </div>
                      {paymentLinksData.razorpay_info.sms_notify_enabled ? (
                        <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                          ✓ Enabled
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full font-medium">
                          Disabled
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-emerald-700 mb-3">
                      SMS sent directly to customer's phone by Razorpay
                    </div>
                    {/* Resend SMS Button */}
                    {paymentLinksData.current_payment_status !== 'paid' && 
                     paymentLinksData.razorpay_info.status === 'created' && (
                      <Button
                        size="sm"
                        onClick={handleResendSms}
                        disabled={resendingSms}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="resend-sms-btn"
                      >
                        {resendingSms ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Phone className="h-4 w-4 mr-2" />
                            Resend SMS via Razorpay
                          </>
                        )}
                      </Button>
                    )}
                    {paymentLinksData.current_payment_status === 'paid' && (
                      <div className="text-xs text-emerald-600 font-medium">
                        ✓ Payment completed - no need to resend
                      </div>
                    )}
                  </div>
                )}

                {/* Summary Card */}
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-900">Total Links Sent</span>
                    <span className="text-lg font-bold text-blue-600">{paymentLinksData.total_links_sent}</span>
                  </div>
                  {paymentLinksData.current_payment_status === 'paid' && (
                    <div className="flex items-center gap-1 mt-2 text-emerald-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Current link is paid</span>
                    </div>
                  )}
                </div>
                
                {/* Payment Links List */}
                {paymentLinksData.payment_links.map((link, index) => (
                  <div 
                    key={link.id || index} 
                    className={`border rounded-lg p-4 ${
                      link.is_current ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          link.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          link.status === 'expired' ? 'bg-red-100 text-red-700' :
                          link.status === 'superseded' ? 'bg-gray-100 text-gray-500' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {paymentLinksData.total_links_sent - index}
                        </div>
                        <span className="font-medium text-gray-900">{link.package_name || 'Unknown Package'}</span>
                        {link.is_current && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-medium">Current</span>
                        )}
                      </div>
                      <span className="font-semibold text-gray-900">₹{link.amount?.toLocaleString() || '—'}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {link.created_at ? formatDateTime(link.created_at) : 'Unknown date'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {link.created_by || 'Unknown'}
                      </div>
                    </div>
                    
                    {link.no_of_inspections && (
                      <div className="text-xs text-gray-500 mb-2">
                        <Car className="h-3 w-3 inline mr-1" />
                        {link.no_of_inspections} inspection{link.no_of_inspections > 1 ? 's' : ''}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        {/* Status Badge */}
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          link.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          link.status === 'created' ? 'bg-amber-100 text-amber-700' :
                          link.status === 'attempted' ? 'bg-orange-100 text-orange-700' :
                          link.status === 'expired' ? 'bg-red-100 text-red-700' :
                          link.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                          link.status === 'superseded' ? 'bg-gray-100 text-gray-500' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {link.status === 'paid' ? '✓ Paid' :
                           link.status === 'created' ? '⏳ Pending' :
                           link.status === 'attempted' ? '⚠ Attempted' :
                           link.status === 'expired' ? '✗ Expired' :
                           link.status === 'cancelled' ? '✗ Cancelled' :
                           link.status === 'superseded' ? 'Superseded' :
                           link.status || 'Unknown'}
                        </span>
                        
                        {/* Sent via badges */}
                        {link.sent_via?.length > 0 && (
                          <div className="flex items-center gap-1">
                            {link.sent_via.includes('whatsapp') && (
                              <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded flex items-center gap-0.5">
                                <MessageCircle className="h-3 w-3" /> WhatsApp
                              </span>
                            )}
                            {link.sent_via.includes('email') && (
                              <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded flex items-center gap-0.5">
                                <Mail className="h-3 w-3" /> Email
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      {link.payment_link && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => window.open(link.payment_link, '_blank')}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                          >
                            <ExternalLink className="h-3 w-3" /> Open
                          </button>
                          <button
                            onClick={async () => {
                              await navigator.clipboard.writeText(link.payment_link);
                              toast.success('Link copied!');
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-0.5"
                          >
                            <Copy className="h-3 w-3" /> Copy
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Inspections created from this payment */}
                    {link.inspections?.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-gray-100">
                        <div className="text-xs font-medium text-gray-600 mb-1">Inspections Created:</div>
                        <div className="space-y-1">
                          {link.inspections.map((insp) => (
                            <div key={insp.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                              <span className="font-medium">{insp.car_number || 'Vehicle pending'}</span>
                              <span className={`px-1.5 py-0.5 rounded ${
                                insp.inspection_status === 'INSPECTION_COMPLETED' ? 'bg-emerald-100 text-emerald-700' :
                                insp.inspection_status === 'INSPECTION_IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {insp.inspection_status?.replace('INSPECTION_', '').replace(/_/g, ' ') || 'New'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="border-t pt-4 flex-shrink-0">
            <Button 
              variant="outline" 
              onClick={() => setIsPaymentLinksModalOpen(false)} 
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Employee Modal */}
      <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="assign-employee-modal">
          <DialogHeader className="border-b pb-4">
            <DialogTitle>Reassign Lead</DialogTitle>
            {assigningLead?.city && (
              <p className="text-sm text-gray-500 mt-1">
                Lead City: <span className="font-medium text-blue-600">{assigningLead.city}</span>
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Assign To (Sales Executives for {assigningLead?.city || 'this city'})</Label>
              {loadingSalesReps ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  <span className="ml-2 text-sm text-gray-500">Loading sales reps...</span>
                </div>
              ) : salesRepsForCity.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    No sales executives found for city: <strong>{assigningLead?.city}</strong>
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Please configure sales executives with this city in HR → Employees → Leads Management tab
                  </p>
                </div>
              ) : (
                <Select value={selectedEmployee || 'unassigned'} onValueChange={(v) => setSelectedEmployee(v === 'unassigned' ? '' : v)}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Select Sales Executive" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">-- Select Sales Executive --</SelectItem>
                    {salesRepsForCity.map((rep) => (
                      <SelectItem key={rep.id} value={rep.id}>
                        {rep.name} {rep.leads_cities?.length > 0 && `(${rep.leads_cities.join(', ')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Reason *</Label>
              <Select value={reassignReason} onValueChange={setReassignReason}>
                <SelectTrigger className="h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Agent on leave">Agent on leave</SelectItem>
                  <SelectItem value="Customer request">Customer request</SelectItem>
                  <SelectItem value="Workload balancing">Workload balancing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsAssignModalOpen(false)} className="flex-1">Cancel</Button>
              <Button 
                onClick={handleAssignEmployee} 
                disabled={saving || !selectedEmployee || !reassignReason || salesRepsForCity.length === 0} 
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Reassign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={isReminderModalOpen} onOpenChange={setIsReminderModalOpen}>
        <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="reminder-modal">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-purple-600" /> {reminderLead?.reminder_date ? 'Edit' : 'Add'} Reminder
            </DialogTitle>
            {reminderLead?.name && (
              <p className="text-sm text-gray-500 mt-1">{reminderLead.name} · {reminderLead.mobile}</p>
            )}
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Current reminder set by */}
            {reminderLead?.reminder_set_by_name && reminderLead?.reminder_date && (
              <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-100 text-xs text-purple-700">
                <User className="h-3 w-3" />
                Current reminder set by <span className="font-medium">{reminderLead.reminder_set_by_name}</span>
                {reminderLead.reminder_set_at && (
                  <span className="text-purple-500">· {new Date(reminderLead.reminder_set_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Date</Label>
                <Input type="date" value={reminderFormData.reminder_date} onChange={(e) => setReminderFormData({ ...reminderFormData, reminder_date: e.target.value })} className="h-10" data-testid="reminder-date-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Time</Label>
                <Select value={reminderFormData.reminder_time || 'select'} onValueChange={(v) => setReminderFormData({ ...reminderFormData, reminder_time: v === 'select' ? '' : v })}>
                  <SelectTrigger className="h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="select">-- Select --</SelectItem>
                    {['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Reason</Label>
              <Select value={reminderFormData.reminder_reason || 'select'} onValueChange={(v) => setReminderFormData({ ...reminderFormData, reminder_reason: v === 'select' ? '' : v })}>
                <SelectTrigger className="h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="select">-- Select --</SelectItem>
                  <SelectItem value="RNR">RNR</SelectItem>
                  <SelectItem value="RCB_WHATSAPP">RCB WhatsApp</SelectItem>
                  <SelectItem value="CALL_BACK">Call Back</SelectItem>
                  <SelectItem value="FOLLOW_UP">Follow Up</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Notes</Label>
              <textarea value={reminderFormData.notes} onChange={(e) => setReminderFormData({ ...reminderFormData, notes: e.target.value })}
                className="w-full min-h-[80px] px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="reminder-notes-input" />
            </div>

            {/* Reminder History Log */}
            {reminderLead?.reminder_history?.length > 0 && (
              <div className="space-y-2 pt-2 border-t" data-testid="reminder-history">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5" /> Reminder History ({reminderLead.reminder_history.length})
                </Label>
                <div className="max-h-[200px] overflow-y-auto space-y-2">
                  {[...reminderLead.reminder_history].reverse().map((entry, idx) => (
                    <div key={idx} className="flex gap-3 p-2.5 bg-gray-50 rounded-lg border text-xs" data-testid={`reminder-history-${idx}`}>
                      <div className="flex-shrink-0 w-1 rounded-full bg-purple-300" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">
                            {entry.date}{entry.time ? `, ${entry.time}` : ''}
                          </span>
                          {entry.reason && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-medium">
                              {entry.reason.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                        {entry.notes && <p className="text-gray-500 mt-0.5 truncate">{entry.notes}</p>}
                        <p className="text-gray-400 mt-1">
                          Set by {entry.set_by_name || 'Unknown'}
                          {entry.set_at && ` · ${new Date(entry.set_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsReminderModalOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSaveReminder} disabled={saving} className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600" data-testid="save-reminder-button">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save Reminder
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>



      {/* Notes & Activities Drawer */}
      <Sheet open={isNotesDrawerOpen} onOpenChange={setIsNotesDrawerOpen}>
        <SheetContent className="sm:max-w-[500px] p-0 flex flex-col h-full" data-testid="notes-drawer">
          <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-slate-50 to-slate-100 flex-shrink-0">
            <SheetTitle className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
                {selectedLeadForNotes?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-gray-900">{selectedLeadForNotes?.name}</div>
                <div className="text-xs text-gray-500 font-normal">{selectedLeadForNotes?.mobile}</div>
              </div>
            </SheetTitle>
          </SheetHeader>

          <Tabs value={notesTab} onValueChange={setNotesTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 mx-6 my-3 flex-shrink-0" style={{ width: 'calc(100% - 48px)' }}>
              <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="notes-tab">
                <StickyNote className="h-4 w-4" /> Notes
              </TabsTrigger>
              <TabsTrigger value="activities" className="flex items-center gap-2" data-testid="activities-tab">
                <Activity className="h-4 w-4" /> Activity Log
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 min-h-0 overflow-hidden relative">
              <TabsContent value="notes" className="absolute inset-0 flex flex-col m-0 overflow-hidden">
              {/* Add Note Section */}
              <div className="bg-slate-50 rounded-xl p-4 border mx-6 mt-2 mb-2 flex-shrink-0">
                <Label className="text-sm font-medium mb-2 block">Add a note</Label>
                <div className="flex gap-2">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Write your note here..."
                    className="flex-1 min-h-[60px] px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="new-note-input"
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button 
                    onClick={handleAddNote} 
                    disabled={savingNote || !newNote.trim()}
                    size="sm"
                    className="bg-gradient-to-r from-blue-600 to-blue-700"
                    data-testid="add-note-button"
                  >
                    {savingNote ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                    Add Note
                  </Button>
                </div>
              </div>

              {/* Notes List */}
              <div className="flex-1 min-h-0 relative">
                {loadingNotes ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : leadNotes.length === 0 ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                    <StickyNote className="h-10 w-10 mb-2 text-gray-300" />
                    <p className="text-sm">No notes yet. Add the first note!</p>
                  </div>
                ) : (
                  <ScrollArea className="absolute inset-0">
                    <div className="space-y-3 px-6 py-2 pr-4">
                    {leadNotes.map((note) => (
                      <div key={note.id} className="bg-white border rounded-xl p-4 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-xs flex-shrink-0">
                            {note.user_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-medium text-sm text-gray-900">{note.user_name || 'Unknown'}</span>
                              <span className="text-xs text-gray-400">{formatDateTime(note.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.note}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </TabsContent>

              <TabsContent value="activities" className="absolute inset-0 m-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="space-y-3 px-6 py-2 pr-4">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : leadActivities.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <Activity className="h-10 w-10 mb-2 text-gray-300" />
                  <p className="text-sm">No activities recorded yet.</p>
                </div>
              ) : (
                <>
                  {leadActivities.map((activity) => {
                    // Determine activity icon and color based on action type
                    const getActivityStyle = (action) => {
                      switch(action) {
                        case 'lead_created': return { bg: 'bg-green-500', icon: '✨' };
                        case 'lead_assigned': 
                        case 'assigned': 
                        case 'reassigned': return { bg: 'bg-blue-500', icon: '👤' };
                        case 'status_changed': return { bg: 'bg-purple-500', icon: '🔄' };
                        case 'note_added': return { bg: 'bg-amber-500', icon: '📝' };
                        case 'payment_link_sent':
                        case 'payment_link_sent_whatsapp': return { bg: 'bg-emerald-500', icon: '💳' };
                        case 'customer_message': return { bg: 'bg-cyan-500', icon: '💬' };
                        case 'reminder_set': return { bg: 'bg-orange-500', icon: '⏰' };
                        case 'whatsapp_sent':
                        case 'bot_response': return { bg: 'bg-green-600', icon: '📱' };
                        case 'whatsapp_reminder_sent': return { bg: 'bg-teal-500', icon: '🔔' };
                        default: return { bg: 'bg-slate-400', icon: '📋' };
                      }
                    };
                    
                    const style = getActivityStyle(activity.action);
                    
                    // Format activity description with full details
                    const getActivityDescription = () => {
                      switch(activity.action) {
                        case 'lead_created':
                          return (
                            <div>
                              <span className="font-medium text-green-700">Lead created</span>
                              {activity.details && <span className="text-gray-600"> - {activity.details}</span>}
                              {activity.new_value && (
                                <div className="mt-1 text-xs bg-green-50 p-2 rounded border border-green-100">
                                  {activity.new_value}
                                </div>
                              )}
                            </div>
                          );
                        case 'lead_assigned':
                        case 'assigned':
                          return (
                            <div>
                              <span className="font-medium text-blue-700">Assigned</span>
                              {activity.new_value && <span className="text-gray-700"> → <span className="font-medium">{activity.new_value}</span></span>}
                              {activity.details && <div className="text-xs text-gray-500 mt-0.5">{activity.details}</div>}
                            </div>
                          );
                        case 'reassigned':
                          return (
                            <div>
                              <span className="font-medium text-blue-700">Reassigned</span>
                              {activity.old_value && <span className="text-gray-600"> from <span className="font-medium">{activity.old_value}</span></span>}
                              {activity.new_value && <span className="text-gray-700"> → <span className="font-medium">{activity.new_value}</span></span>}
                            </div>
                          );
                        case 'status_changed':
                          return (
                            <div>
                              <span className="font-medium text-purple-700">Status changed</span>
                              <span className="text-gray-600"> from </span>
                              <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-medium">{activity.old_value || 'N/A'}</span>
                              <span className="text-gray-600"> → </span>
                              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">{activity.new_value}</span>
                            </div>
                          );
                        case 'note_added':
                          return (
                            <div>
                              <span className="font-medium text-amber-700">Note added</span>
                              {activity.new_value && (
                                <div className="mt-1 text-xs bg-amber-50 p-2 rounded border border-amber-100 text-gray-700">
                                  "{activity.new_value}"
                                </div>
                              )}
                            </div>
                          );
                        case 'payment_link_sent':
                        case 'payment_link_sent_whatsapp':
                          return (
                            <div>
                              <span className="font-medium text-emerald-700">Payment link sent</span>
                              {activity.action === 'payment_link_sent_whatsapp' && <span className="text-gray-500"> via WhatsApp</span>}
                              {activity.new_value && (
                                <div className="mt-1 text-xs bg-emerald-50 p-2 rounded border border-emerald-100">
                                  <span className="font-medium">Amount:</span> {activity.new_value}
                                </div>
                              )}
                              {activity.details && <div className="text-xs text-gray-500 mt-0.5">{activity.details}</div>}
                            </div>
                          );
                        case 'customer_message':
                          return (
                            <div>
                              <span className="font-medium text-cyan-700">Customer message received</span>
                              {activity.new_value && (
                                <div className="mt-1 text-xs bg-cyan-50 p-2 rounded border border-cyan-100 text-gray-700">
                                  "{activity.new_value}"
                                </div>
                              )}
                            </div>
                          );
                        case 'whatsapp_sent':
                        case 'bot_response':
                        case 'whatsapp_reminder_sent':
                          return (
                            <div>
                              <span className="font-medium text-green-700">
                                {activity.action === 'bot_response' ? 'Bot responded' : activity.action === 'whatsapp_reminder_sent' ? 'WhatsApp reminder sent' : 'WhatsApp sent'}
                              </span>
                              {activity.new_value && (
                                <div className="mt-1 text-xs bg-green-50 p-2 rounded border border-green-100 text-gray-700">
                                  "{activity.new_value}"
                                </div>
                              )}
                              {activity.details && activity.action === 'whatsapp_reminder_sent' && (
                                <div className="text-xs text-gray-500 mt-1">{activity.details}</div>
                              )}
                            </div>
                          );
                        case 'reminder_set':
                          return (
                            <div>
                              <span className="font-medium text-orange-700">Reminder set</span>
                              {activity.new_value && <span className="text-gray-600"> for <span className="font-medium">{activity.new_value}</span></span>}
                            </div>
                          );
                        default:
                          return (
                            <div>
                              <span className="font-medium text-gray-700">{activity.action?.replace(/_/g, ' ')}</span>
                              {activity.details && <span className="text-gray-600"> - {activity.details}</span>}
                              {activity.new_value && <div className="text-xs text-gray-500 mt-0.5">{activity.new_value}</div>}
                            </div>
                          );
                      }
                    };
                    
                    return (
                      <div key={activity.id} className="relative pl-6 pb-3 border-l-2 border-slate-200 last:pb-0">
                        <div className={`absolute -left-2 top-0 h-4 w-4 rounded-full ${style.bg} border-2 border-white flex items-center justify-center text-[8px]`}>
                          {style.icon}
                        </div>
                        <div className="bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                              {activity.action?.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(activity.created_at)}</span>
                          </div>
                          <div className="text-sm text-gray-700">
                            {getActivityDescription()}
                          </div>
                          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
                            <div className="h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-[10px] font-medium">
                              {activity.user_name?.charAt(0)?.toUpperCase() || 'S'}
                            </div>
                            <span className="text-xs text-gray-500">by <span className="font-medium">{activity.user_name || 'System'}</span></span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
                </div>
              </ScrollArea>
            </TabsContent>
            </div>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Ad Info Modal - Shows ad details when clicking the info icon */}
      <Dialog open={adInfoModal.open} onOpenChange={(open) => setAdInfoModal({ ...adInfoModal, open })}>
        <DialogContent className="sm:max-w-[450px]" data-testid="ad-info-modal">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-600" />
              Ad Information
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {adInfoModal.lead && (
              <>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-24 text-xs font-medium text-gray-500 uppercase">Source</div>
                    <div className="text-sm font-medium text-gray-900">{adInfoModal.lead.source || 'Unknown'}</div>
                  </div>
                  
                  {adInfoModal.lead.ad_id && (
                    <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                      <div className="flex-shrink-0 w-24 text-xs font-medium text-blue-600 uppercase">Ad ID</div>
                      <div className="text-sm font-mono text-blue-800 break-all">{adInfoModal.lead.ad_id}</div>
                    </div>
                  )}
                  
                  {adInfoModal.lead.ad_name && (
                    <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                      <div className="flex-shrink-0 w-24 text-xs font-medium text-purple-600 uppercase">Ad Name</div>
                      <div className="text-sm text-purple-800">{adInfoModal.lead.ad_name}</div>
                    </div>
                  )}
                  
                  {adInfoModal.lead.campaign_name && (
                    <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                      <div className="flex-shrink-0 w-24 text-xs font-medium text-green-600 uppercase">Campaign</div>
                      <div className="text-sm text-green-800">{adInfoModal.lead.campaign_name}</div>
                    </div>
                  )}
                  
                  {adInfoModal.lead.ad_set_name && (
                    <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                      <div className="flex-shrink-0 w-24 text-xs font-medium text-orange-600 uppercase">Ad Set</div>
                      <div className="text-sm text-orange-800">{adInfoModal.lead.ad_set_name}</div>
                    </div>
                  )}

                  {!adInfoModal.lead.ad_id && !adInfoModal.lead.ad_name && (
                    <div className="text-center py-4 text-gray-500">
                      <Info className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">No ad information available for this lead</p>
                    </div>
                  )}
                </div>
                
                <div className="flex justify-end pt-2 border-t">
                  <Button variant="outline" onClick={() => setAdInfoModal({ open: false, lead: null })}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
